import { ArrowLeft, RotateCw, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getUserByToken, sendVerificationCodePhone } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { CANANA_COUNTRY_CODE } from '../../../constants/canadaCountryCode';
import { useModal } from '../../hooks/modalContext';
import { showToast } from '../../utils/showToast';
import { sanitisePhoneNumber } from '../../utils/phoneNumber';
import { formatPhoneNumber } from '../../utils/utils';
import UpdatePhone from '../UpdatePhone/UpdatePhone';
import VerifyPhoneCode from '../UpdatePhone/VerifyPhoneCode';
import styles from '../ResetPasswordModal/ResetPasswordModal.module.css';

type PhoneView = 'phone-input' | 'verify-code';

export function PhoneSettingsModal() {
  const { setModal, modalState } = useModal();
  const { auth, currentUser, setCurrentUser } = useAuth();
  const mode = modalState?.phoneSettingsMode ?? 'edit';
  const [view, setView] = useState<PhoneView>(mode === 'verify' ? 'verify-code' : 'phone-input');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [resending, setResending] = useState(false);
  const codeSentRef = useRef(false);

  useEffect(() => {
    const existingPhone =
      mode === 'add'
        ? ''
        : sanitisePhoneNumber(currentUser?.user?.phoneNumber ?? '').slice(-10);
    setPhoneNumber(existingPhone);
    setView(mode === 'verify' ? 'verify-code' : 'phone-input');
    codeSentRef.current = false;
  }, [currentUser?.user?.phoneNumber, mode]);

  useEffect(() => {
    if (mode !== 'verify' || !currentUser?.user?.phoneNumber || !auth?.data.token) return;
    if (codeSentRef.current) return;

    void sendVerificationCodePhone(
      `+${CANANA_COUNTRY_CODE}${currentUser.user.phoneNumber}`,
      4,
      auth.data.token
    ).catch((error) => {
      const isBlockedPhone = error instanceof Error && error.message === 'phone_number_blocked';
      showToast({
        message: isBlockedPhone
          ? 'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
          : 'Failed to send verification code. Please try again.',
        type: 'error',
      });
    });
    codeSentRef.current = true;
  }, [auth?.data.token, currentUser?.user?.phoneNumber, mode]);

  const handleClose = () => setModal(null);

  const handleSuccess = async () => {
    if (auth?.data.token) {
      try {
        const { data } = await getUserByToken(auth.data.token);
        setCurrentUser(data);
      } catch {
        // Profile refresh will still fetch latest tenant details.
      }
    }
    showToast({
      message: 'Phone number verified successfully',
      type: 'success',
    });
    setModal(null, { refreshProfile: true });
  };

  const handleResendCode = async () => {
    if (!auth?.data.token || !phoneNumber) return;
    setResending(true);
    try {
      await sendVerificationCodePhone(
        `+${CANANA_COUNTRY_CODE}${phoneNumber}`,
        4,
        auth.data.token
      );
      showToast({ message: 'Verification code sent', type: 'success' });
    } catch (error) {
      const isBlockedPhone = error instanceof Error && error.message === 'phone_number_blocked';
      showToast({
        message: isBlockedPhone
          ? 'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
          : 'Failed to resend code. Please try again.',
        type: 'error',
      });
    } finally {
      setResending(false);
    }
  };

  const title =
    mode === 'add'
      ? 'Add phone number'
      : mode === 'verify'
        ? 'Verify phone number'
        : 'Update phone number';

  const handleBack = () => {
    if (view === 'verify-code' && mode !== 'verify') {
      setView('phone-input');
      return;
    }
    handleClose();
  };

  return (
    <main className={styles.main}>
      <div className={styles.headingOuter}>
        <div className='flex items-center gap-3'>
          <ArrowLeft className='cursor-pointer text-[#0055FF]' size={20} onClick={handleBack} />
          <span>{title}</span>
        </div>
        <X className={styles.crossIcon} size={24} onClick={handleClose} stroke='#0E0E0B' />
      </div>
      <div className={styles.contentOuter}>
        {view === 'phone-input' && (
          <UpdatePhone
            embedded
            phoneNumber={phoneNumber}
            onSuccess={(phone) => {
              setPhoneNumber(phone);
              setView('verify-code');
            }}
          />
        )}
        {view === 'verify-code' && (
          <>
            <VerifyPhoneCode
              embedded
              phoneNumber={phoneNumber}
              mode='verify'
              onSuccess={() => void handleSuccess()}
            />
            <button
              type='button'
              className='mt-4 flex items-center justify-center gap-2 text-[13px] font-semibold text-[#0055FF] hover:underline disabled:opacity-50'
              onClick={() => void handleResendCode()}
              disabled={resending}
            >
              <RotateCw size={14} />
              Resend code
            </button>
            {mode === 'verify' && (
              <button
                type='button'
                className='mt-2 text-[13px] font-semibold text-[#0055FF] hover:underline'
                onClick={() => {
                  setView('phone-input');
                }}
              >
                Use a different number ({formatPhoneNumber(phoneNumber)})
              </button>
            )}
          </>
        )}
      </div>
    </main>
  );
}
